import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { DocumentResult } from "../../../../../models/document-result";
import { Registration } from "../../../../../models/registration";
import { Requirement } from "../../../../../models/requirement";
import { RequirementControllerService } from "../../../../../services/requirement-controller/requirement-controller.service";
import { DocumentResultControllerService } from "../../../../../services/result-controller/document-result-controller.service";
import { ResultService } from "../../../../../services/result-controller/result.service";
import { Store } from "../../../../../services/store.service";
import { TranslatedBaseError } from "../../../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { map } from "rxjs/internal/operators/map";

import { RecoverOrphanConfig } from "../../../../../models/recover-orphan-config";

@Component({
  selector: "registration-result",
  templateUrl: "./result-registration.component.html",
  providers: [DocumentResultControllerService, ToastrService],
})
export class RegistrationResultComponent extends TranslatedBaseError implements OnChanges {

  @Input() public registration: Registration;
  @Input() public serviceId: string;
  @Input() public disabled: boolean = false;
  @Input() public determinantMapById: { [key: string]: string };
  @Input() public debugErrors: RecoverOrphanConfig[] = [];
  @Output() public modalClosedEvent = new EventEmitter();
  @Output() public havePhysicalEvent: EventEmitter<boolean> = new EventEmitter();
  public requirements: Requirement[] = [];
  public documentResultList: any[] = [];
  public documentResultListLength: number;
  public showDeterminant: boolean[] = [];

  public searchRequirementFilter: any = {
    name: null,
  };

  public documentResult: any;
  public addNew: boolean = false;
  public showDeterminantBuilder: boolean = false;
  public showList: boolean = false;

  public isCollapsed: boolean = false;

  public subscription: any;

  constructor(private documentResultControllerService: DocumentResultControllerService,
    private requirementService: RequirementControllerService,
    private resultService: ResultService,
    public store: Store,
    toastr: ToastrService,
    public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.subscription = this.resultService.update.subscribe((data) => {
      if (data.update) {
        this.reloadDocumentResultList();
      }
    });

  }

  public ngOnChanges() {
    this.reloadDocumentResultList();
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public dropped(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

      // component logic
      if (!this.disabled) {
        this.documentResultList.forEach((it, index, itemsArray) => {
          itemsArray[index].sortOrder = index;
          itemsArray[index].registrationId = this.registration.id;
          itemsArray[index].requirementId = itemsArray[index].requirement.id;
        });
        this.sortDocumentResultList();
      }
    }
  }

  public sortDocumentResultList() {
    if (!this.disabled) {
      this.documentResultControllerService.updateSortOrder(this.documentResultList).subscribe(
        (data) => this.responseSuccessful("Results reordered!"),
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service", error),
      );
    }
  }

  public reloadDocumentResultList() {
    if (this.registration && this.registration.id) {
      this.documentResultControllerService.getRegistrationResults(this.registration.id).subscribe(
        (data: any) => {
          const deletedItems = [];
          for (const existingItem of this.documentResultList) {
            const keepThis = data.some((regDocRes) => {
              return regDocRes.id === existingItem.id;
            });
            if (!keepThis) { deletedItems.push(existingItem); }
          }
          deletedItems.forEach((item) => {
            this.documentResultList.splice(this.documentResultList.indexOf(item), 1);
          });
          for (const regDocRes of data) {
            const existingItem = this.documentResultList.find((item) => {
              return item.id === regDocRes.id;
            });
            if (regDocRes.linkingFileUploadPath) {
              regDocRes.linkingFileUploadPathRouterLink =
                this.routerLinkForLinkingFileUploadPath(regDocRes.linkingFileUploadPath);
              regDocRes.linkingFileUploadPathTitle =
                regDocRes.linkingFileUploadPath.substring(regDocRes.linkingFileUploadPath.lastIndexOf("#") + 1);
            }
            if (existingItem) {
              this.documentResultList[this.documentResultList.indexOf(existingItem)] = regDocRes;
            } else {
              this.documentResultList.push(regDocRes);
            }
          }
          this.updateData(this.documentResultList);
          this.havePhysicalEvent.emit(this.havePhysicalResults());
        },
        (error) => this.showErrorMessage(error),
      );
      this.searchRequirementFilter.name = null;
    }
  }

  public havePhysicalResults(): boolean {
    if (this.documentResultList.length === 0) { return false; }
    const physicalResults = this.documentResultList.filter((r) => r.resultIsPhysical);
    return physicalResults.length > 0;
  }

  public updateData(data) {
    this.showDeterminant = new Array(data.length).fill(false);
    this.loadServiceRequirements(data);
  }

  public loadServiceRequirements(documentResultList: any) {
    this.requirementService.getServiceRequirements().subscribe(
      (data) => {
        this.removeRequirementsOverlappingWithDocumentRequirementsOrDocumentResults(
          documentResultList,
          data);
      },
      (error) => this.showErrorMessageWithTitle("Error HTTP Post Service", error),
    );
  }

  public removeRequirementsOverlappingWithDocumentRequirementsOrDocumentResults(documentRequirementsOrDocumentResults: any[], requirements: Requirement[]) {
    for (let j = documentRequirementsOrDocumentResults.length - 1; j >= 0; j--) {
      for (let i = requirements.length - 1; i >= 0; i--) {
        if (requirements[i].id === documentRequirementsOrDocumentResults[j].requirement.id) {
          requirements.splice(i, 1);
        }
      }
    }
    this.requirements = requirements.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
    });
    this.documentResultList = documentRequirementsOrDocumentResults;
    this.documentResultListLength = documentRequirementsOrDocumentResults.length;
  }

  public openEdit(result: any, determinantView = false, name?: string) {
    if (!this.disabled) {
      this.showList = false;
      this.showDeterminantBuilder = determinantView;
      if (result) {
        this.documentResult = result;
        this.addNew = false;
      } else {
        this.documentResult = new DocumentResult();
        if (name) {
          this.documentResult.requirement.name = name;
        } else if (this.searchRequirementFilter.name !== null) {
          this.documentResult.requirement.name = this.searchRequirementFilter.name;
        }
        this.addNew = true;
      }
      this.updateEditData();
    }
  }

  public determinantAllowed(item): boolean {
    return !item.linkingFileUploadPath;
  }

  public hasDeterminant(item) {
    if (!item.jsonDeterminants || item.jsonDeterminants === '[{"type":"OR","items":[]}]') {
      return false;
    }
    return true;
  }

  public hasError(id: string): boolean {
    return this.debugErrors && this.debugErrors.some((error) => error.objectId === id);
  }

  public removeDeterminant(result, index) {
    if (!this.disabled) {
      result.registrationId = this.registration.id;
      result.jsonDeterminants = "";
      this.documentResultControllerService.saveRegistrationResult(result).subscribe(
        (data) => {
          this.showDeterminant[index] = false;
          this.responseSuccessful("Result updated");
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
      );
    }
  }

  public deleteDocumentResult(isConfirmed: boolean, documentResultId: string) {
    if (!this.disabled) {
      if (isConfirmed) {
        this.documentResultControllerService.deleteRegistrationResult(documentResultId).subscribe(
          (data) => this.reloadDocumentResultList(),
        );
      }
    }
  }

  public fixVisualEffectOnDocumentResultSelected(requirement): void {
    this.showList = false;
    const documentResult: DocumentResult = new DocumentResult();
    documentResult.requirement = requirement;
    documentResult.requirementId = requirement.id;
    this.documentResultList.push(documentResult);
  }

  public addDocumentResult(requirement) {
    if (!this.disabled) {
      const documentResult = new DocumentResult();
      documentResult.registrationId = this.registration.id;
      documentResult.requirementId = requirement.id;

      // for visual effect
      this.fixVisualEffectOnDocumentResultSelected(requirement);

      this.documentResultControllerService.addRegistrationResult(this.registration.id, documentResult).subscribe(
        (data) => {
          this.responseSuccessful("Result added!");
          this.showList = false;
          this.reloadDocumentResultList();
        },
        (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
      );
    }
  }

  public showOrAdd() {
    if (!this.disabled) {
      if (this.requirements.length > 0) {
        this.showList = !this.showList;
      } else {
        this.openEdit(false);
      }
    }
  }

  public updateEditData() {
    if (!this.disabled) {
      this.store.state.common.sections.type = "result";
      this.resultService.serviceId = this.serviceId;
      this.resultService.registration = this.registration;
      this.resultService.documentResult = JSON.parse(JSON.stringify(this.documentResult)); // mini-hack so the list doesn't update when working in edit layer
      this.resultService.addNew = this.addNew;
      this.resultService.showDeterminantBuilder = this.showDeterminantBuilder;
      this.resultService.initEdit();
    }
  }

  public routerLinkForLinkingFileUploadPath(value: string): string {
    if (!value) { return ""; }
    return "/services/" + value.replace("#", "/roles/");
  }

  public addDocumentResults(items: any[]): void {
    const promises = [];
    items.forEach(item => {
      this.fixVisualEffectOnDocumentResultSelected(item);
      const documentResult = new DocumentResult();
      documentResult.registrationId = this.registration.id;
      documentResult.requirementId = item.id;
      promises.push(
        this.documentResultControllerService.addRegistrationResult(this.registration.id, documentResult).pipe(map((data) => data)).toPromise()
      );
    });
    Promise.all<any[]>(promises).then((results: any[]) => {
      this.responseSuccessful("Result added!");
      this.showList = false;
      this.reloadDocumentResultList();
    });
  }

}
